import { repeat } from "./polyfills.js";

export const roundToNearestIncrement = (num, field) => {
	let evalValue = num;
	if(num < 0) {
		evalValue = num * -1;
	}

	if(evalValue === null) {
		return;
	}

	let step = field.IncrementingStep || 1;

	if(field.RoundingPoint) {
		step = Number(`0.${repeat("0", field.RoundingPoint - 1)}1`);
	}

	const deltaFromLow = roundToFourDigits(evalValue % step);
	const low = roundToFourDigits(evalValue - deltaFromLow);
	const high = roundToFourDigits(low + step);

	const lowDelta = roundToFourDigits(evalValue - low);
	const highDelta = roundToFourDigits(high - evalValue);

	let retVal = lowDelta >= highDelta ? high : low;
	if(num < 0) {
		retVal *= -1;
	}

	if(!field.IsZeroPadded) {
		return retVal;
	}

	return retVal.toFixed(field.RoundingPoint);

	function roundToFourDigits(dec) {
		if(dec.toString().indexOf("e") > -1) {
			return Number(Math.round(dec) + "e-4");
		}
		return Number(Math.round(dec + "e4") + "e-4");
	}
};

// this is for when a user is in the process of inputting a number
export const isValidPartialNumber = (num) => /^-?(?:[0-9]{0,12})(\.[0-9]{0,12})?$/.test(num);
// this is for when a user has input a value in a number field, and navigates away from it
export const isValidCompleteNumber = (num) => num !== ""
	&& num !== "-"
	&& num !== "."
	&& num !== "-."
	&& isValidPartialNumber(num)
	&& !isNaN(num);

export const isNumberWithinConstraints = (value, numericConstraints) => {
	const isValidNumber = typeof value === "number"
		? isFinite(value)
		: isValidCompleteNumber(value);

	if(!isValidNumber) {
		return false;
	}

	const numericValue = Number(value);
	const minimumInclusive = numericConstraints.minimumInclusive;
	const minimumExclusive = numericConstraints.minimumExclusive;
	const maximumInclusive = numericConstraints.maximumInclusive;
	const maximumExclusive = numericConstraints.maximumExclusive;

	return (minimumInclusive === undefined || numericValue >= minimumInclusive)
		&& (minimumExclusive === undefined || numericValue > minimumExclusive)
		&& (maximumInclusive === undefined || numericValue <= maximumInclusive)
		&& (maximumExclusive === undefined || numericValue < maximumExclusive);
};

const COMPLETION_EPSILON = 1e-12;

const getEffectiveBound = (inclusiveValue, exclusiveValue, isMinimum) => {
	if(inclusiveValue === undefined && exclusiveValue === undefined) {
		return undefined;
	}

	if(inclusiveValue === undefined) {
		return { value: exclusiveValue, inclusive: false };
	}

	if(exclusiveValue === undefined) {
		return { value: inclusiveValue, inclusive: true };
	}

	if(inclusiveValue === exclusiveValue) {
		return { value: inclusiveValue, inclusive: false };
	}

	const inclusiveIsMoreRestrictive = isMinimum
		? inclusiveValue > exclusiveValue
		: inclusiveValue < exclusiveValue;

	return inclusiveIsMoreRestrictive
		? { value: inclusiveValue, inclusive: true }
		: { value: exclusiveValue, inclusive: false };
};

export const getEffectiveNumericBounds = (numericConstraints) => ({
	minimum: getEffectiveBound(
		numericConstraints.minimumInclusive,
		numericConstraints.minimumExclusive,
		true
	),
	maximum: getEffectiveBound(
		numericConstraints.maximumInclusive,
		numericConstraints.maximumExclusive,
		false
	)
});

const isWithinEffectiveBounds = (value, bounds) => {
	if(bounds.minimum && (value < bounds.minimum.value
		|| (!bounds.minimum.inclusive && value === bounds.minimum.value))) {
		return false;
	}

	if(bounds.maximum && (value > bounds.maximum.value
		|| (!bounds.maximum.inclusive && value === bounds.maximum.value))) {
		return false;
	}

	return true;
};

const getCompletionRanges = (value) => {
	const ranges = [];
	const addRange = (minimum, maximum, step) => ranges.push({ minimum, maximum, step });

	if(value === "") {
		addRange(0, 0, 1);
		for(let digits = 1; digits <= 12; digits++) {
			const minimum = 10 ** (digits - 1);
			const maximum = (10 ** digits) - 1;
			addRange(minimum, maximum, 1);
			for(let decimalDigits = 1; decimalDigits <= 12; decimalDigits++) {
				const step = 10 ** -decimalDigits;
				addRange(minimum, maximum + 1 - step, step);
			}
		}
		for(let decimalDigits = 1; decimalDigits <= 12; decimalDigits++) {
			const step = 10 ** -decimalDigits;
			addRange(0, 1 - step, step);
		}
		return ranges;
	}

	if(value.startsWith(".")) {
		const fraction = value.slice(1);
		const fractionLength = fraction.length;
		const fractionValue = fraction ? Number(`0.${fraction}`) : 0;
		const firstDecimalLength = Math.max(1, fractionLength);

		for(let decimalDigits = firstDecimalLength; decimalDigits <= 12; decimalDigits++) {
			const step = 10 ** -decimalDigits;
			const scale = 10 ** (decimalDigits - fractionLength);
			const minimum = fractionValue;
			const maximum = minimum + (scale - 1) * step;
			addRange(minimum, maximum, step);
		}
		return ranges;
	}

	const decimalIndex = value.indexOf(".");
	const integerPart = decimalIndex === -1 ? value : value.slice(0, decimalIndex);
	const fraction = decimalIndex === -1 ? undefined : value.slice(decimalIndex + 1);
	const integerValue = Number(integerPart || 0);

	if(fraction === undefined) {
		for(let appendedDigits = 0; appendedDigits <= 12 - integerPart.length; appendedDigits++) {
			const multiplier = 10 ** appendedDigits;
			const integerMinimum = integerValue === 0 ? 0 : integerValue * multiplier;
			const integerMaximum = integerValue === 0
				? multiplier - 1
				: (integerValue + 1) * multiplier - 1;
			addRange(integerMinimum, integerMaximum, 1);

			for(let decimalDigits = 1; decimalDigits <= 12; decimalDigits++) {
				const step = 10 ** -decimalDigits;
				addRange(integerMinimum, integerMaximum + 1 - step, step);
			}
		}
		return ranges;
	}

	const fractionLength = fraction.length;
	const fractionValue = fraction ? Number(`0.${fraction}`) : 0;
	for(let decimalDigits = Math.max(1, fractionLength); decimalDigits <= 12; decimalDigits++) {
		const step = 10 ** -decimalDigits;
		const scale = 10 ** (decimalDigits - fractionLength);
		const minimum = integerValue + fractionValue;
		const maximum = minimum + (scale - 1) * step;
		addRange(minimum, maximum, step);
	}

	if(!fraction) {
		addRange(integerValue, integerValue, 1);
	}

	return ranges;
};

const hasReachableValue = (range, bounds) => {
	let minimum = range.minimum;
	let maximum = range.maximum;

	if(bounds.minimum) {
		minimum = Math.max(minimum, bounds.minimum.value);
		if(!bounds.minimum.inclusive && minimum === bounds.minimum.value) {
			minimum += COMPLETION_EPSILON;
		}
	}

	if(bounds.maximum) {
		maximum = Math.min(maximum, bounds.maximum.value);
		if(!bounds.maximum.inclusive && maximum === bounds.maximum.value) {
			maximum -= COMPLETION_EPSILON;
		}
	}

	if(minimum > maximum + COMPLETION_EPSILON) {
		return false;
	}

	let firstStep = Math.ceil((minimum - range.minimum) / range.step - COMPLETION_EPSILON);
	let candidate = range.minimum + Math.max(0, firstStep) * range.step;

	if(bounds.minimum && !bounds.minimum.inclusive && candidate <= bounds.minimum.value + COMPLETION_EPSILON) {
		firstStep += 1;
		candidate = range.minimum + Math.max(0, firstStep) * range.step;
	}

	if(bounds.maximum && (!bounds.maximum.inclusive
		? candidate >= bounds.maximum.value - COMPLETION_EPSILON
		: candidate > bounds.maximum.value + COMPLETION_EPSILON)) {
		return false;
	}

	return candidate <= maximum + COMPLETION_EPSILON;
};

const getSignedCompletionRanges = (value) => {
	const isNegative = value.startsWith("-");
	const unsignedValue = isNegative ? value.slice(1) : value;
	const ranges = getCompletionRanges(unsignedValue);

	return isNegative
		? ranges.map(range => ({
			minimum: -range.maximum,
			maximum: -range.minimum,
			step: range.step
		}))
		: ranges;
};

export const canCompleteNumberWithinConstraints = (value, numericConstraints) => {
	if(value === "") {
		return true;
	}

	if(!isValidPartialNumber(value)) {
		return false;
	}

	const bounds = getEffectiveNumericBounds(numericConstraints);
	if(bounds.minimum && bounds.maximum
		&& (bounds.minimum.value > bounds.maximum.value
			|| (bounds.minimum.value === bounds.maximum.value
				&& (!bounds.minimum.inclusive || !bounds.maximum.inclusive)))) {
		return false;
	}

	return getSignedCompletionRanges(value).some(range => hasReachableValue(range, bounds));
};

const isStepAligned = (value, step) => Math.abs(value / step - Math.round(value / step)) < COMPLETION_EPSILON;

const getFirstValueAtOrAbove = (value, inclusive, step) => {
	let result = Math.ceil(value / step - COMPLETION_EPSILON) * step;
	if(!inclusive && isStepAligned(result, step)) {
		result += step;
	}
	return result;
};

const getFirstValueAtOrBelow = (value, inclusive, step) => {
	let result = Math.floor(value / step + COMPLETION_EPSILON) * step;
	if(!inclusive && isStepAligned(result, step)) {
		result -= step;
	}
	return result;
};

export const getFirstConstrainedValue = (direction, numericConstraints, incrementingStep) => {
	const step = Math.abs(incrementingStep || 1);
	const bounds = getEffectiveNumericBounds(numericConstraints);
	let value = direction === "up"
		? bounds.minimum
			? getFirstValueAtOrAbove(bounds.minimum.value, bounds.minimum.inclusive, step)
			: step
		: bounds.maximum
			? getFirstValueAtOrBelow(bounds.maximum.value, bounds.maximum.inclusive, step)
			: -step;

	if(!isWithinEffectiveBounds(value, bounds)) {
		value = direction === "up"
			? bounds.maximum && getFirstValueAtOrBelow(bounds.maximum.value, bounds.maximum.inclusive, step)
			: bounds.minimum && getFirstValueAtOrAbove(bounds.minimum.value, bounds.minimum.inclusive, step);
	}

	return value !== false && value !== undefined && isWithinEffectiveBounds(value, bounds)
		? value
		: undefined;
};

const getFormattedConstrainedValue = (value, numericConstraints, digitCount) => {
	if(value === undefined || typeof value !== "number" || !isFinite(value)) {
		return undefined;
	}

	const formattedValue = value.toFixed(digitCount || 0);
	return isNumberWithinConstraints(formattedValue, numericConstraints)
		? formattedValue
		: undefined;
};

const getConstrainedSpinnerValue = (direction, currentValue, numericConstraints, incrementingStep, digitCount) => {
	const step = Math.abs(incrementingStep || 1);
	const hasCurrentValue = currentValue !== ""
		&& isValidCompleteNumber(currentValue)
		&& isNumberWithinConstraints(currentValue, numericConstraints);
	let value = hasCurrentValue
		? Number(currentValue) + (direction === "up" ? step : -step)
		: getFirstConstrainedValue(direction, numericConstraints, step);

	if(value !== undefined && !isNumberWithinConstraints(value, numericConstraints)) {
		if(direction === "up" && numericConstraints.maximumInclusive !== undefined) {
			value = numericConstraints.maximumInclusive;
		}
		else if(direction === "down" && numericConstraints.minimumInclusive !== undefined) {
			value = numericConstraints.minimumInclusive;
		}
		else {
			return undefined;
		}
	}

	return getFormattedConstrainedValue(value, numericConstraints, digitCount);
};

export const getNextConstrainedValue = (currentValue, numericConstraints, incrementingStep, digitCount) =>
	getConstrainedSpinnerValue("up", currentValue, numericConstraints, incrementingStep, digitCount);

export const getPreviousConstrainedValue = (currentValue, numericConstraints, incrementingStep, digitCount) =>
	getConstrainedSpinnerValue("down", currentValue, numericConstraints, incrementingStep, digitCount);

export const getNumericConstraintMessage = (numericConstraints) => {
	const bounds = getEffectiveNumericBounds(numericConstraints);
	const minimumText = bounds.minimum
		&& `${bounds.minimum.inclusive ? "at least" : "greater than"} ${bounds.minimum.value}`;
	const maximumText = bounds.maximum
		&& `${bounds.maximum.inclusive ? "at most" : "less than"} ${bounds.maximum.value}`;

	if(minimumText && maximumText) {
		return `Enter a value ${minimumText} and ${maximumText}.`;
	}
	if(minimumText) {
		return `Enter a value ${minimumText}.`;
	}
	if(maximumText) {
		return `Enter a value ${maximumText}.`;
	}
	return "Enter a valid number.";
};


export const roundToRoundingPoint = (val, field) => {
	const places = field.RoundingPoint;
	
	if(places === undefined)
		return val;

	// don't mutate if already less precise
	if(getPrecision(val) < places)
		return val;

	return +val.toFixed(places);
};

export function getPrecision(num) {
	num = parseFloat(num); 
	if(isNaN(num))
		return num;
	
	if(!isFinite(num))
		return 0;

	let e = 1, p = 0;

	while(Math.round(num * e) / e !== num) {
		e *= 10;
		p++;
	}

	return p;
}



